import { inject, Injectable, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  SelectedFile,
  SelectRawFilesUtil,
  type SimpleFileTypeEnum,
  UploadFileStatusEnum,
} from '@reconcept-platform/shared/util';
import { forkJoin, type Observable, of, switchMap, take } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { UploadApi } from '../api/upload/upload.api';

@Injectable()
export class SelectFilesControlHelper {
  private selectFileUtil = inject(SelectRawFilesUtil);
  private uploadApi = inject(UploadApi);
  private translateService = inject(TranslateService);

  allowMultiple: boolean;
  maxFileSize: number; //bytes
  requiredContentType: SimpleFileTypeEnum;

  // new version of file upload does not use an empty SelectedFile as a createbutton, because it's confusing
  useEmptyFile: boolean;

  // value kept by control
  selectedFiles = signal<SelectedFile[]>([]);

  init(config: {
    useEmptyFile?: boolean;
    allowMultiple?: boolean;
    maxFileSize?: number;
    requiredContentType?: SimpleFileTypeEnum;
  }) {
    // @ts-expect-error TS2322 type not assignable
    this.allowMultiple = config.allowMultiple;
    // @ts-expect-error TS2322 type not assignable
    this.maxFileSize = config.maxFileSize;
    // @ts-expect-error TS2322 type not assignable
    this.requiredContentType = config.requiredContentType;
    // @ts-expect-error TS2322 type not assignable
    this.useEmptyFile = config.useEmptyFile;

    // make sure there's an empty file
    this.updateFilesList();
  }

  writeControlValue(selectedFiles: SelectedFile[]) {
    // check if only value is not null
    if (selectedFiles && selectedFiles.length && !!selectedFiles[0]) {
      this.selectedFiles.set([...selectedFiles]);
    } else {
      // make sure we have at least an empty array
      this.selectedFiles.set([]);
    }

    // make sure there's an empty file at the end
    this.updateFilesList();
  }

  getValue(): SelectedFile[] {
    // filter out the empty files and files with error
    const filteredFiles = this.selectedFiles().filter((file) => file.hasFile && !file.hasError());

    // return array of values
    return [...filteredFiles];
  }

  getValidation() {
    // check if there are items with errors that do not have the remove status
    const filesWithErrors = this.selectedFiles().filter((file) => !file.willBeRemoved && file.hasError());

    return !filesWithErrors.length
      ? null
      : {
          fileUploadError: {
            valid: false,
            message: filesWithErrors[0].errorMessage,
          },
        };
  }

  removeFile(removableFile: SelectedFile, removeFromList?: boolean) {
    if (removeFromList) {
      // filter existing files
      this.selectedFiles.update((selectedFiles) => selectedFiles.filter((file) => file !== removableFile));
    } else {
      this.selectedFiles.update((selectedFiles) => {
        return selectedFiles.map((file) => {
          if (file === removableFile) {
            file.willBeRemoved = true;
            // @ts-expect-error TS2322 type not assignable
            file.directUploadId = null;
          }
          return file;
        });
      });
    }

    // make sure there's an empty file at the end
    this.updateFilesList();
  }

  // if allow multiple, we always want there to be one extra empty file at the end of the list
  private updateFilesList() {
    // remove all empty files
    this.selectedFiles.update((selectedFiles) => selectedFiles.filter((file) => file.hasFile));

    // insert one empty file
    const lastFile = this.selectedFiles()[this.selectedFiles().length - 1];
    if (this.useEmptyFile && (!lastFile || (lastFile.hasFile && this.allowMultiple))) {
      this.insertFile(new SelectedFile());
    }
  }

  insertFile(file: SelectedFile) {
    this.selectedFiles.update((selectedFiles) => (this.allowMultiple ? [...selectedFiles, file] : [file]));
  }

  processFiles(files: File[]): Observable<SelectedFile[]> {
    //upload and emit
    return this.selectRawFiles(files).pipe(
      switchMap((newlySelectedFiles) => this.uploadOrDestroySelectedFiles(newlySelectedFiles)),
    );
  }

  selectRawFiles(selectedFileBlobs: File[], targetFile?: SelectedFile): Observable<SelectedFile[]> {
    return this.selectFileUtil
      .selectRawFiles(selectedFileBlobs, this.allowMultiple, this.maxFileSize, this.requiredContentType, targetFile)
      .pipe(
        tap((files: SelectedFile[]) => {
          if (targetFile) {
            // we need to keep the files array unmodified
            const copyFiles = [...files];
            copyFiles.splice(files.indexOf(targetFile), 1); // this keeps the target file the same
            copyFiles.forEach((file) => this.insertFile(file)); // insert more selected files
          } else {
            files.forEach((file) => this.insertFile(file));
          }
        }),
        tap(() => this.updateFilesList()), // make sure there's an empty file at the end
      );
  }

  uploadOrDestroySelectedFiles(newlySelectedFiles: SelectedFile[]): Observable<SelectedFile[]> {
    return this.uploadAndDestroySelectedFiles(newlySelectedFiles).pipe(take(1));
  }

  // returns files added
  uploadAndDestroySelectedFiles(filesOrUndefined: SelectedFile[] | undefined) {
    const files = filesOrUndefined ?? [];
    const updatedFiles = files.filter((file) => file.status !== UploadFileStatusEnum.existing) ?? []; // filter out existing files

    const filesToDestroy = files.filter((file) => file.willBeRemoved) ?? [],
      filesToAdd = updatedFiles.filter((file) => !file.willBeRemoved);

    return this.createAndDestroyFiles(filesToDestroy, filesToAdd);
  }

  // returns files added
  private createAndDestroyFiles(filesToDestroy: SelectedFile[], filesToAdd: SelectedFile[]) {
    const errorMessage = this.translateService.instant('qz-something-went-wrong-while-uploading-your-file-zm');

    const filesToDestroy$: Observable<unknown[]> = filesToDestroy.length
      ? forkJoin(filesToDestroy.map((file) => this.uploadApi.destroyAttachedFile(file.id)))
      : of([]);

    // @ts-expect-error TS2322 type not assignable
    const filesToAdd$: Observable<SelectedFile[]> = filesToAdd.length
      ? this.uploadApi.createDirectUploadsAndUploadFiles(filesToAdd, errorMessage)
      : of([]);

    return forkJoin([filesToDestroy$, filesToAdd$]).pipe(map(() => filesToAdd));
  }
}
